/**
 * The wrap-up of a finished plan as steps (DOM-free): five steps in order, each with its state, the one line that says
 * who has it and what it needs, and the action it offers; the card's header line; the header button's words.
 *
 * Where the facts come from: the plan (`PlanState`, with the host's `wrapUp` block, shared/protocol/wrapUp.ts), and the
 * branch's standing as `planMergeOffer` reads it. The words are shared/wording/planLoop.ts. The Plans view, the
 * conversation that made the plan, the agent cards and Home all read this one model, so they never disagree.
 */
import { WRAP_UP_START, WRAP_UP_STEPS } from '../wording/planLoop.js';
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
export const criteriaWords = (n) => plural(n, 'criterion', 'criteria');
/** A criterion counts as passed only while its verdict is not older than the finished work. */
export function criterionPassed(a) {
    return a.status === 'passed' && !a.stale;
}
export function reportWriter(plan) {
    const w = plan.wrapUp;
    const manager = w?.manager;
    const options = w?.writers ?? [];
    if (manager?.running)
        return { name: manager.name, manager, options, pick: false };
    const stand = w?.scribe ?? options[0];
    return { ...(stand ? { name: stand } : {}), ...(manager ? { manager } : {}), options, pick: options.length > 1 };
}
/** The line a criterion shows when nothing can check it for the person. */
export const UNCHECKABLE = 'No way to check this from here: confirm it yourself, or mark it not met.';
export function wrapUpModel({ plan, merge, mergeText }) {
    const w = plan.wrapUp;
    const closed = !!plan.unify || plan.loop === 'complete';
    const waived = new Set(w?.waived ?? []);
    const writer = reportWriter(plan);
    const hint = (id) => WRAP_UP_STEPS.find((s) => s.id === id);
    // 1. The report.
    const reportDone = !!w?.reportAt || !!plan.hold?.report;
    let report;
    if (waived.has('report'))
        report = { state: 'not-needed', line: 'Skipped: the plan closes without an outcome report.' };
    else if (reportDone)
        report = { state: 'done', line: 'The outcome report is written. It says what shipped against what was planned.' };
    else if (w?.running)
        report = { state: 'running', line: `${w.running.by} is writing the outcome report.` };
    else if (writer.manager?.running)
        report = { state: 'waiting', line: `The operations manager (${writer.manager.name}) writes the outcome report.` };
    else if (writer.manager) {
        report = writer.options.length
            ? { state: 'waiting', line: `The operations manager (${writer.manager.name}) is not running. ${writer.name} can write it instead, or you can close without one.` }
            : { state: 'waiting', line: `The operations manager (${writer.manager.name}) is not running. Start the team so it can write the report, or close without one.` };
    }
    else if (writer.options.length)
        report = { state: 'waiting', line: `No manager on this team: ${w?.scribe ?? (writer.pick ? 'a specialist you pick' : writer.name)} can write it, or you can close without one.` };
    else
        report = { state: 'not-needed', line: 'Nobody on this team can write the report, so this step is skipped. You can still close the plan.' };
    // 2. The criteria.
    const acceptance = plan.acceptance ?? [];
    const passed = acceptance.filter(criterionPassed).length;
    const open = acceptance.length - passed;
    const uncheckable = acceptance.filter((a) => !criterionPassed(a) && !a.checkId).map((a) => a.id);
    let criteria;
    if (acceptance.length === 0)
        criteria = { state: 'not-needed', line: 'This plan set no acceptance criteria.' };
    else if (open === 0)
        criteria = { state: 'done', line: `All ${plural(acceptance.length, 'criterion', 'criteria')} met.` };
    else if (w?.verifying)
        criteria = { state: 'running', line: `${w.verifier ?? 'An agent'} is checking ${criteriaWords(open)}.` };
    else
        criteria = { state: 'waiting', line: `${open} of ${acceptance.length} still to verify: confirm each one or mark it not met${w?.verifier && plan.intentId ? `, or ask ${w.verifier} to run the checks` : ''}.` };
    const askVerify = w?.verifier && plan.intentId && open > 0 && !w.verifying ? { by: w.verifier, label: `Ask ${w.verifier} to verify` } : undefined;
    // 3. The follow-ups.
    const followUps = w?.followUps ?? [];
    let follow;
    if (followUps.length)
        follow = { state: 'waiting', line: `${plural(followUps.length, 'follow-up task')} the team added ${followUps.length === 1 ? 'is' : 'are'} still open: finish ${followUps.length === 1 ? 'it' : 'them'}, move to the backlog, or say not needed.` };
    else if (plan.followUps?.total)
        follow = { state: 'done', line: `All ${plural(plan.followUps.total, 'follow-up task')} finished.` };
    else
        follow = { state: 'not-needed', line: 'The team added no follow-up tasks.' };
    // 4. The review: files read, and where the work goes.
    const files = w?.review;
    const readAll = !!files && files.files > 0 && files.reviewed >= files.files;
    const mergeSettled = merge === undefined || merge === 'merged' || merge === 'gone';
    let review;
    if (waived.has('review'))
        review = { state: 'not-needed', line: 'Skipped: the changes are not reviewed here.' };
    else if (!plan.branch)
        review = { state: 'not-needed', line: 'This plan has no branch of its own to review.' };
    else {
        const read = files ? (files.files === 0 ? 'No changed files' : `${files.reviewed} of ${files.files} files reviewed`) : 'Not opened yet';
        const where = merge === 'ready' || merge === 'dirty' || merge === 'elsewhere' ? ` · ${(mergeText ?? 'the work is not merged yet').replace(/\.\s*$/, '')}` : merge === 'merged' ? ' · merged' : merge === 'gone' ? ' · branch removed' : '';
        const finished = (readAll || files?.files === 0) && mergeSettled;
        review = { state: finished ? 'done' : 'waiting', line: `${read}${where}.` };
    }
    // 5. Close.
    const states = {
        report,
        criteria,
        followups: follow,
        review,
        close: { state: closed ? 'done' : 'waiting', line: '' },
    };
    const before = ['report', 'criteria', 'followups', 'review'].map((id) => states[id]);
    const readyToClose = before.every((s) => s.state === 'done' || s.state === 'not-needed');
    states.close.line = closed
        ? 'The plan is closed.'
        : readyToClose
            ? 'Everything above is done. Closing makes the plan Complete, files its retrospective with the decisions, and frees the team.'
            : 'Close it when the steps above are done, or close it anyway with a reason, which is recorded on the plan.';
    const steps = WRAP_UP_STEPS.map((s) => ({ id: s.id, label: s.label, hint: hint(s.id).hint, state: states[s.id].state, line: states[s.id].line }));
    const done = steps.filter((s) => s.state === 'done' || s.state === 'not-needed').length;
    // The header: the first thing that waits.
    let next;
    if (closed)
        next = 'closed';
    else if (report.state === 'running')
        next = `${w.running.by} is writing the report`;
    else if (report.state === 'waiting')
        next = 'waiting for you to start it';
    else if (criteria.state === 'running')
        next = `${w.verifier ?? 'an agent'} is verifying ${criteriaWords(open)}`;
    else if (criteria.state === 'waiting')
        next = `waiting for you to verify ${criteriaWords(open)}`;
    else if (follow.state === 'waiting')
        next = `waiting for you to settle ${plural(followUps.length, 'follow-up task')}`;
    else if (review.state === 'waiting')
        next = !readAll && files?.files ? `waiting for you to review ${files.files - files.reviewed} ${files.files - files.reviewed === 1 ? 'file' : 'files'}` : merge && merge !== 'merged' && merge !== 'gone' ? 'waiting for you to merge the work' : 'waiting for you to review the changes';
    else
        next = 'ready to close';
    const header = closed ? 'Wrap-up · done' : `Wrap-up · ${done} of ${steps.length} steps done · ${next}`;
    const waitingOnYou = !closed && steps.some((s) => s.state === 'waiting');
    const reportCanStart = report.state === 'waiting' || report.state === 'running';
    const button = reportCanStart && !closed ? { label: report.state === 'running' ? `Running — ${w.running.by} is writing the report` : WRAP_UP_START, running: report.state === 'running' } : undefined;
    return { steps, done, total: steps.length, header, next, waitingOnYou, readyToClose, closed, ...(button ? { button } : {}), writer, criteria: { total: acceptance.length, passed, open }, followUps, ...(askVerify ? { askVerify } : {}), uncheckable };
}
/** Whether a plan shows a wrap-up at all: its work is finished and it is not closed (the host sends `wrapUp` exactly then). */
export function hasWrapUp(plan) {
    return !!plan.wrapUp && !plan.unify && plan.status !== 'abandoned';
}
//# sourceMappingURL=wrapUpModel.js.map